/**
 * A standalone copy of the classic MuiButton overrides (see themes/muiTheme.ts),
 * reproduced here as a plain emotion-styled button rather than a live reference to the
 * MUI theme. Deliberately independent: this is meant to be adapted for the new-UX visual
 * language over time without needing to touch (or risk affecting) the classic Button
 * every other part of the app still uses. Values below are a snapshot as of the copy —
 * they will not follow future edits to muiTheme.ts.
 *
 * Fixes the font-family bug plain `styled('button')` elements had (browsers don't
 * inherit font-family into form elements by default) by setting it explicitly, matching
 * MuiButton's actual behavior instead of falling back to the OS UI font.
 */
import styled from '@emotion/styled';
import React from 'react';
import { tokens } from './tokens';

type Variant = 'contained' | 'outlined' | 'text';
type Color = 'primary' | 'secondary' | 'neutral';
type Size = 'medium' | 'small';

// Copied literal values — see themes/muiTheme.ts for the classic theme's source of truth.
// primary/primaryDark are the exception: these read from the kit's own blue600/blue700
// tokens, not a classic-theme snapshot.
const palette = {
  grey300: '#dde0e3',
  grey400: '#bbc1c7',
  grey700: '#494f55',
  primaryMain: tokens.color.blue600,
  primaryDark: tokens.color.blue700,
  secondaryMain: '#e25e50',
  secondaryDark: '#cb3120',
  white: '#ffffff'
};

const variantColorStyles = (variant: Variant, color: Color) => {
  if (variant === 'contained') {
    if (color === 'primary') {
      return {
        color: palette.white,
        background: palette.primaryMain,
        border: '1px solid transparent',
        '&:hover': {
          background: palette.primaryDark,
          boxShadow: '0 0 0 4px rgba(103,178,244,.1)'
        }
      };
    }
    if (color === 'secondary') {
      return {
        color: palette.white,
        background: palette.secondaryMain,
        border: '1px solid transparent',
        '&:hover': {
          background: palette.secondaryDark,
          boxShadow: '0 0 0 4px rgba(247,123,110,.1)'
        }
      };
    }
    return {
      color: palette.grey700,
      background: palette.grey300,
      border: '1px solid transparent',
      '&:hover': {
        background: palette.grey400,
        boxShadow: '0 0 0 4px rgba(119,128,136,.1)'
      }
    };
  }
  if (variant === 'outlined') {
    const c =
      color === 'primary'
        ? palette.primaryMain
        : color === 'secondary'
        ? palette.secondaryMain
        : palette.grey700;
    const borderColor = color === 'neutral' ? palette.grey300 : c;
    const ringRgba =
      color === 'primary'
        ? 'rgba(103,178,244,.1)'
        : color === 'secondary'
        ? 'rgba(247,123,110,.1)'
        : 'rgba(119,128,136,.1)';
    return {
      color: c,
      background: 'transparent',
      border: `1px solid ${borderColor}`,
      '&:hover': { boxShadow: `0 0 0 4px ${ringRgba}` }
    };
  }
  // text
  const c =
    color === 'primary'
      ? palette.primaryMain
      : color === 'secondary'
      ? palette.secondaryMain
      : palette.grey700;
  return {
    color: c,
    background: 'transparent',
    border: '1px solid transparent'
  };
};

const sizeStyles = (variant: Variant, size: Size) => {
  // text keeps its own, more compact padding — only contained/outlined must match.
  if (variant === 'text') {
    return size === 'small'
      ? { fontSize: '13px', padding: '6px 10px' }
      : { fontSize: '15px', padding: '10px 14px' };
  }
  return size === 'small'
    ? { fontSize: '13px', padding: '8px 12px' }
    : { fontSize: '15px', padding: '10px 14px' };
};

const StyledButton = styled('button')<{
  cVariant: Variant;
  cColor: Color;
  cSize: Size;
}>`
  font-family: ebony, sans-serif;
  font-weight: ${(p) => (p.cVariant === 'contained' ? 400 : 200)};
  line-height: 20px;
  text-transform: initial;
  white-space: nowrap;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
  ${(p) => {
    const v = variantColorStyles(p.cVariant, p.cColor);
    const s = sizeStyles(p.cVariant, p.cSize);
    return { ...v, ...s };
  }}
  &:disabled {
    cursor: default;
    opacity: 0.5;
    box-shadow: none !important;
  }
  &:focus {
    box-shadow: none;
  }
  /* New-UX house style: thinner icon strokes than the lucide/classic default of 2. */
  & svg {
    stroke-width: 1.5;
  }
`;

export const NewUxButton = React.forwardRef<
  HTMLButtonElement,
  // HTMLAttributes has a legacy non-standard `color?: string` (old <font color> leftover)
  // that collides with our own `color?: Color` below — omit it so ours wins cleanly.
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> & {
    variant?: Variant;
    color?: Color;
    size?: Size;
    startIcon?: React.ReactNode;
  }
>(
  (
    { variant = 'text', color = 'neutral', size = 'medium', startIcon, children, ...rest },
    ref
  ) => (
    <StyledButton
      ref={ref}
      type="button"
      cVariant={variant}
      cColor={color}
      cSize={size}
      {...rest}
    >
      {startIcon}
      {children}
    </StyledButton>
  )
);
